import {
  assistantCmsDraftDataSchema,
  canonicalJson,
  utf8Bytes,
} from "@/wab/shared/copilot/assistant-cms-draft";
import { cmsRowPatchEditsSchema } from "@/wab/shared/copilot/assistant-cms-row-patch";
import {
  cmsSchemaChangeNoteSchema,
  cmsSchemaChangesSchema,
} from "@/wab/shared/copilot/assistant-cms-schema-change";
import { assistantCallSchema } from "@/wab/shared/copilot/assistant-protocol";
import { z } from "zod";

/**
 * Hosted CMS structure and row-lifecycle operations (the native editor's table,
 * schema, row delete/clone, bulk create and nested-edit surface). One durable
 * prepare -> execute -> read/cancel/recover family shaped like the publication
 * family: every effect binds actor, project, original context and request; the
 * request itself carries the schema/row preconditions (digests, revisions) that
 * are compared under the native locks. Nothing here publishes or fires
 * webhooks.
 */
const id = z.string().min(1).max(500);
const digest = z.string().regex(/^[a-f0-9]{64}$/);

export const CMS_OPERATION_BOUNDS = {
  leaseMilliseconds: 60_000,
  livePreparations: 128,
  listPage: 16,
  /** The journal stores the request twice (request + prepare call) plus three contexts. */
  requestUtf8Bytes: 64_000,
  journalBytes: 262_144,
  rowTargets: 25,
  rowsCreate: 25,
  readRows: 8,
  tablesPage: 100,
} as const;

const rowTarget = z
  .object({ rowId: id, revision: z.number().int().nonnegative(), sourceDigest: digest })
  .strict();
const uniqueRows = (targets: { rowId: string }[]) =>
  new Set(targets.map((t) => t.rowId)).size === targets.length;
const rowTargets = z
  .array(rowTarget)
  .min(1)
  .max(CMS_OPERATION_BOUNDS.rowTargets)
  .refine(uniqueRows, "Duplicate row target");

const requestUnion = z.discriminatedUnion("operation", [
  /** Native createTable: name + identifier (the identifier is normalized and immutable afterwards). */
  z
    .object({
      operation: z.literal("table_create"),
      databaseId: id,
      name: z.string().min(1).max(1000),
      identifier: z.string().min(1).max(200),
      description: z.string().max(5000).nullable().optional(),
    })
    .strict(),
  /** Native model name/description, preview URL (webhooks are never touched) and archive/unarchive. */
  z
    .object({
      operation: z.literal("table_update"),
      databaseId: id,
      tableId: id,
      tableDigest: digest,
      name: z.string().min(1).max(1000).optional(),
      description: z.string().max(5000).nullable().optional(),
      previewUrl: z.string().max(2000).nullable().optional(),
      archived: z.boolean().optional(),
    })
    .strict(),
  /** Native deleteCmsTable: soft delete; rows and references are neither cascaded nor blocked. */
  z
    .object({ operation: z.literal("table_delete"), databaseId: id, tableId: id, tableDigest: digest })
    .strict(),
  z
    .object({
      operation: z.literal("schema_update"),
      databaseId: id,
      tableId: id,
      schemaDigest: digest,
      changes: cmsSchemaChangesSchema,
    })
    .strict(),
  z
    .object({ operation: z.literal("row_delete"), databaseId: id, tableId: id, targets: rowTargets })
    .strict(),
  z
    .object({
      operation: z.literal("row_clone"),
      databaseId: id,
      tableId: id,
      targets: z
        .array(rowTarget.extend({ identifier: z.string().min(1).max(200).optional() }).strict())
        .min(1)
        .max(CMS_OPERATION_BOUNDS.rowTargets)
        .refine(uniqueRows, "Duplicate row target"),
    })
    .strict(),
  z
    .object({
      operation: z.literal("rows_create"),
      databaseId: id,
      tableId: id,
      schemaDigest: digest,
      rows: z
        .array(
          z
            .object({ identifier: z.string().min(1).max(200), draftData: assistantCmsDraftDataSchema })
            .strict(),
        )
        .min(1)
        .max(CMS_OPERATION_BOUNDS.rowsCreate),
    })
    .strict(),
  z
    .object({
      operation: z.literal("row_patch"),
      databaseId: id,
      tableId: id,
      schemaDigest: digest,
      rowId: id,
      revision: z.number().int().nonnegative(),
      sourceDigest: digest,
      edits: cmsRowPatchEditsSchema,
    })
    .strict(),
]);
export const cmsOperationRequestSchema = requestUnion
  .refine(
    (r) =>
      r.operation !== "table_update" ||
      r.name !== undefined ||
      r.description !== undefined ||
      r.previewUrl !== undefined ||
      r.archived !== undefined,
    "Empty table change",
  )
  .refine(
    (r) => utf8Bytes(canonicalJson(r)) <= CMS_OPERATION_BOUNDS.requestUtf8Bytes,
    "CMS operation request exceeds the durable byte bound",
  );
export type CmsOperationRequest = z.infer<typeof cmsOperationRequestSchema>;
export type CmsOperationName = CmsOperationRequest["operation"];
export const CMS_OPERATION_NAMES = [
  "table_create",
  "table_update",
  "table_delete",
  "schema_update",
  "row_delete",
  "row_clone",
  "rows_create",
  "row_patch",
] as const;

/** Tool arguments are `{ request }`: the registry needs a root object schema, the request is a union. */
export const cmsOperationArgumentsSchema = z.object({ request: cmsOperationRequestSchema }).strict();

export const cmsOperationItemSchema = z
  .object({
    rowId: id,
    action: z.enum(["deleted", "cloned", "created", "patched"]),
    sourceRowId: id.nullable(),
    beforeRevision: z.number().int().nonnegative().nullable(),
    afterRevision: z.number().int().nonnegative().nullable(),
    /** Delete only: whether the row had published content when it was deleted. */
    publishedBefore: z.boolean().nullable(),
    afterDigest: digest.nullable(),
  })
  .strict();
export const cmsOperationTableStateSchema = z
  .object({
    identifier: z.string().max(500),
    name: z.string().max(1000),
    description: z.string().max(5000).nullable(),
    previewUrl: z.string().max(2000).nullable(),
    archived: z.boolean(),
    deleted: z.boolean(),
  })
  .strict();

const ITEM_ACTION = {
  row_delete: "deleted",
  row_clone: "cloned",
  rows_create: "created",
  row_patch: "patched",
} as const;
export const cmsOperationReceiptSchema = z
  .object({
    receiptVersion: z.literal(1),
    effectId: id,
    kind: z.literal("cms_operation"),
    operation: z.enum(CMS_OPERATION_NAMES),
    projectId: id,
    workspaceId: id,
    databaseId: id,
    tableId: id,
    schemaDigestBefore: digest.nullable(),
    schemaDigestAfter: digest.nullable(),
    tableDigestAfter: digest.nullable(),
    /** Persisted table state read back after the write. */
    table: cmsOperationTableStateSchema,
    changes: z.array(cmsSchemaChangeNoteSchema).max(50),
    items: z.array(cmsOperationItemSchema).max(CMS_OPERATION_BOUNDS.rowsCreate),
  })
  .strict()
  .superRefine((r, c) => {
    const rowOp = r.operation in ITEM_ACTION;
    const action = ITEM_ACTION[r.operation as keyof typeof ITEM_ACTION];
    const ok =
      (rowOp
        ? r.items.length > 0 &&
          r.items.every((i) => i.action === action) &&
          (r.operation !== "row_patch" || r.items.length === 1) &&
          r.changes.length === 0 &&
          !r.table.deleted
        : r.items.length === 0) &&
      (r.operation === "schema_update"
        ? r.changes.length > 0 && !!r.schemaDigestBefore && !!r.schemaDigestAfter
        : r.operation === "table_create"
          ? r.schemaDigestBefore === null && r.changes.length === 0
          : r.operation === "table_delete"
            ? r.changes.length === 0 && r.table.deleted && r.tableDigestAfter === null
            : r.changes.length === 0) &&
      (r.operation === "table_delete" || r.tableDigestAfter !== null);
    if (!ok)
      c.addIssue({ code: z.ZodIssueCode.custom, message: "Inconsistent native CMS operation receipt" });
  });
export type CmsOperationReceipt = z.infer<typeof cmsOperationReceiptSchema>;

export const cmsOperationPrepareSchema = z
  .object({ effectId: id, request: cmsOperationRequestSchema, call: assistantCallSchema })
  .strict();
export const cmsOperationExecuteSchema = z.object({ effectId: id, call: assistantCallSchema }).strict();
export const cmsOperationRefSchema = z.object({ effectId: id }).strict();
export const cmsOperationListInputSchema = z
  .object({
    cursor: id.nullable().default(null),
    limit: z.number().int().min(1).max(CMS_OPERATION_BOUNDS.listPage).default(CMS_OPERATION_BOUNDS.listPage),
  })
  .strict();
export const cmsOperationReadSchema = z
  .object({
    projectId: id,
    effectId: id,
    requestDigest: digest.nullable(),
    state: z.enum(["prepared", "applied", "cancelled", "expired", "unknown"]),
    outcome: z.enum(["applied", "not_applied", "outcome_unknown"]),
    expiresAt: z.string().datetime().nullable(),
    receipt: cmsOperationReceiptSchema.nullable(),
  })
  .strict()
  .superRefine((r, c) => {
    if (
      (r.receipt && (r.receipt.effectId !== r.effectId || r.receipt.projectId !== r.projectId)) ||
      r.outcome !==
        (r.state === "applied" ? "applied" : r.state === "cancelled" || r.state === "expired" ? "not_applied" : "outcome_unknown") ||
      (r.state === "applied") !== (r.receipt !== null) ||
      (r.state === "unknown") !== (r.requestDigest === null) ||
      (r.state === "prepared" && r.expiresAt === null) ||
      (r.state === "applied" && r.expiresAt !== null) ||
      (r.state === "unknown" && r.expiresAt !== null)
    )
      c.addIssue({ code: z.ZodIssueCode.custom, message: "Inconsistent durable CMS operation state" });
  });
export const cmsOperationJournalEntrySchema = z
  .object({
    request: cmsOperationRequestSchema,
    prepareCall: assistantCallSchema,
    executeCall: assistantCallSchema.nullable(),
    effect: cmsOperationReadSchema,
  })
  .strict();
export const cmsOperationListSchema = z
  .object({
    projectId: id,
    actorUserId: id,
    effects: z.array(cmsOperationJournalEntrySchema).max(CMS_OPERATION_BOUNDS.listPage),
    nextCursor: id.nullable(),
    expiredEffectIds: z.array(id).max(CMS_OPERATION_BOUNDS.listPage),
  })
  .strict();

export const CMS_OPERATION_TOOLS = {
  prepare: "prepare_cms_operation",
  execute: "execute_prepared_cms_operation",
} as const;
/** Client-reported audit identity only; never permission or issuance proof. */
export function cmsOperationAuditCall(
  projectId: string,
  effectId: string,
  request: CmsOperationRequest,
  operation: "prepare" | "execute",
  raw: unknown,
) {
  const call = assistantCallSchema.parse(raw);
  const tool = operation === "prepare" ? CMS_OPERATION_TOOLS.prepare : CMS_OPERATION_TOOLS.execute;
  const args =
    operation === "prepare"
      ? cmsOperationArgumentsSchema.parse(call.arguments)
      : call.arguments;
  if (
    call.toolName !== tool ||
    call.expectedContext.projectId !== projectId ||
    canonicalJson(args) !==
      canonicalJson(operation === "prepare" ? { request } : { preparationId: effectId }) ||
    utf8Bytes(JSON.stringify(call)) > CMS_OPERATION_BOUNDS.requestUtf8Bytes + 16_384
  )
    throw new Error("CMS operation original call binding mismatch");
  return call;
}

export function cmsOperationCanonicalIntent(
  projectId: string,
  actorUserId: string,
  effectId: string,
  rawRequest: unknown,
  context: unknown,
) {
  const request = cmsOperationRequestSchema.parse(rawRequest);
  const canonical = canonicalJson({
    schemaVersion: 1,
    kind: "cms_operation",
    projectId,
    actorUserId,
    effectId,
    request,
    originalContext: context,
  });
  if (utf8Bytes(canonical) > CMS_OPERATION_BOUNDS.journalBytes)
    throw new Error("CMS operation durable byte bound exceeded");
  return { request, canonical };
}
export function cmsOperationStoredCalls(
  projectId: string,
  effectId: string,
  request: CmsOperationRequest,
  context: unknown,
  prepare: unknown,
  execute: unknown,
) {
  const p = cmsOperationAuditCall(projectId, effectId, request, "prepare", prepare);
  if (canonicalJson(p.expectedContext) !== canonicalJson(context))
    throw new Error("CMS operation prepare context changed");
  const e = execute === null ? null : cmsOperationAuditCall(projectId, effectId, request, "execute", execute);
  if (e && canonicalJson(e.expectedContext) !== canonicalJson(context))
    throw new Error("CMS operation execute context changed");
  if (
    utf8Bytes(canonicalJson({ request, originalContext: context, prepareCall: p, executeCall: e })) >
    CMS_OPERATION_BOUNDS.journalBytes
  )
    throw new Error("CMS operation journal bound exceeded");
  return { prepare: p, execute: e };
}

/**
 * Pure native reads that supply the preconditions a request must carry: the
 * table inventory (archived included), a table header with both digests, a
 * stable page of rows with their source digests, and the current draft values
 * of chosen fields/locales. Content permission, like the native editor.
 */
export const cmsOperationSourceInputSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("tables"), databaseId: id }).strict(),
  z.object({ mode: z.literal("table"), databaseId: id, tableId: id }).strict(),
  z
    .object({
      mode: z.literal("rows"),
      databaseId: id,
      tableId: id,
      cursor: id.nullable().default(null),
      limit: z.number().int().min(1).max(CMS_OPERATION_BOUNDS.listPage).default(CMS_OPERATION_BOUNDS.listPage),
    })
    .strict(),
  z
    .object({
      mode: z.literal("row_values"),
      databaseId: id,
      tableId: id,
      rowIds: z.array(id).min(1).max(CMS_OPERATION_BOUNDS.readRows).refine((a) => new Set(a).size === a.length),
      fields: z.array(z.string().min(1).max(500)).min(1).max(100),
      locales: z.array(z.string().max(500)).min(1).max(20),
    })
    .strict(),
]);
/** Root-object export of the discriminated source input for the tool registry. */
export const cmsOperationSourceToolInputSchema = z
  .object({
    mode: z.enum(["tables", "table", "rows", "row_values"]),
    databaseId: id,
    tableId: id.optional(),
    cursor: id.nullable().optional(),
    limit: z.number().int().min(1).max(CMS_OPERATION_BOUNDS.listPage).optional(),
    rowIds: z.array(id).min(1).max(CMS_OPERATION_BOUNDS.readRows).optional(),
    fields: z.array(z.string().min(1).max(500)).min(1).max(100).optional(),
    locales: z.array(z.string().max(500)).min(1).max(20).optional(),
  })
  .strict();
const tableHeader = z
  .object({
    tableId: id,
    identifier: z.string().max(500),
    name: z.string().max(1000),
    description: z.string().max(5000).nullable(),
    previewUrl: z.string().max(2000).nullable(),
    archived: z.boolean(),
    schemaDigest: digest,
    tableDigest: digest,
  })
  .strict();
const rowHeader = z
  .object({
    rowId: id,
    identifier: z.string().max(500).nullable(),
    revision: z.number().int().nonnegative(),
    published: z.boolean(),
    hasDraft: z.boolean(),
    sourceDigest: digest,
  })
  .strict();
const sourceScope = { projectId: id, workspaceId: id, databaseId: id };
export const cmsOperationSourceSchema = z.discriminatedUnion("mode", [
  z
    .object({
      mode: z.literal("tables"),
      ...sourceScope,
      locales: z.array(z.string().max(500)).max(100),
      tables: z.array(tableHeader).max(CMS_OPERATION_BOUNDS.tablesPage),
    })
    .strict(),
  z
    .object({ mode: z.literal("table"), ...sourceScope, locales: z.array(z.string().max(500)).max(100), table: tableHeader })
    .strict(),
  z
    .object({
      mode: z.literal("rows"),
      ...sourceScope,
      tableId: id,
      schemaDigest: digest,
      rows: z.array(rowHeader).max(CMS_OPERATION_BOUNDS.listPage),
      nextCursor: id.nullable(),
    })
    .strict(),
  z
    .object({
      mode: z.literal("row_values"),
      ...sourceScope,
      tableId: id,
      schemaDigest: digest,
      rows: z
        .array(
          rowHeader
            .extend({
              /** locale -> field -> stored draft-base value (draft if present, else published). */
              values: z.record(z.record(z.unknown())),
              /** Requested fields this reader refuses (hidden, unsupported schema, over the value bounds). */
              unreadable: z.array(z.string().max(500)).max(100),
            })
            .strict(),
        )
        .max(CMS_OPERATION_BOUNDS.readRows),
    })
    .strict(),
]);
